import { useEffect } from 'react';
import { useSetAtom } from 'jotai';

import { pendingIntegrationRecentOrdersRefreshesState, transactionsState } from '../../../store';

import type { BaseDocument, Integration, Transaction } from '@rewardopl/types';
import type { SetStateAction } from 'jotai';

type Action = 'append' | 'delete' | 'prepend' | 'update';

type BasePayload<T extends BaseDocument = BaseDocument> = {
  type: string;
  action: Omit<Action, 'delete'>;
  data: T;
};

type DeletePayload<T extends BaseDocument = BaseDocument> = {
  type: string;
  action: 'delete';
  data: Pick<T, '_id'>;
};

type RefreshPayload<T extends BaseDocument = BaseDocument> = {
  type: string;
  action: 'refresh';
  data: T['_id'];
};

type Payload<T extends BaseDocument = BaseDocument> =
  | BasePayload<T>
  | DeletePayload<T>
  | RefreshPayload<T>;

function isDeletePayload<T extends BaseDocument>(payload: Payload<T>): payload is DeletePayload<T> {
  return payload.action === 'delete';
}

function isRefreshPayload(payload: Payload): payload is RefreshPayload {
  return payload.action === 'refresh';
}

function handleAction<T extends BaseDocument>(
  setter: (nextValueOrSetter: SetStateAction<T[]>) => void,
  payload: Exclude<Payload<T>, RefreshPayload<T>>,
) {
  if (isDeletePayload(payload)) {
    const { data: nextData } = payload;

    setter((prevData) => prevData.filter((prevItem) => prevItem._id !== nextData._id));
    return;
  }

  const { action, data: nextData } = payload;

  function isSameDocument(prevItem: T) {
    return prevItem._id === nextData._id;
  }

  function replaceDocument(prevItem: T) {
    return isSameDocument(prevItem) ? nextData : prevItem;
  }

  switch (action) {
    case 'append':
      setter((prevData) =>
        prevData.find(isSameDocument) ? prevData.map(replaceDocument) : [...prevData, nextData],
      );
      break;
    case 'prepend':
      setter((prevData) =>
        prevData.find(isSameDocument) ? prevData.map(replaceDocument) : [nextData, ...prevData],
      );
      break;
    case 'update':
      setter((prevData) => prevData.map(replaceDocument));
      break;
    default:
    // Ignore unknown action types
  }
}

export default function WebsocketHandler() {
  const setTransactions = useSetAtom(transactionsState);
  const setPendingIntegrationRecentOrdersRefreshesState = useSetAtom(
    pendingIntegrationRecentOrdersRefreshesState,
  );

  useEffect(() => {
    let ws: WebSocket;

    function setupWebSocket() {
      ws = new WebSocket(`wss://${window.location.host}/ws`);

      ws.addEventListener('message', (event) => {
        const { data: rawPayload } = event;

        const payload = JSON.parse(rawPayload) as Payload;

        if (isRefreshPayload(payload)) {
          switch (payload.type) {
            case 'integration_order':
              setPendingIntegrationRecentOrdersRefreshesState((prev) => [
                ...prev,
                (payload as RefreshPayload<Integration>).data,
              ]);
              break;
            default:
            // Ignore unknown refresh payload types
          }
        } else {
          switch (payload.type) {
            case 'transaction':
              handleAction(
                setTransactions,
                payload as Exclude<Payload<Transaction>, RefreshPayload>,
              );
              break;
            default:
            // Ignore unknown payload types
          }
        }
      });

      ws.addEventListener('close', (event) => {
        if (!event.wasClean) {
          setTimeout(() => {
            setupWebSocket();
          }, 1000);
        }
      });
    }

    setupWebSocket();

    return () => {
      if (!ws) {
        return;
      }

      ws.close();
    };
  }, [setPendingIntegrationRecentOrdersRefreshesState, setTransactions]);

  return null;
}
